import { MouseEvent } from 'react';
import { Info } from 'lucide-react';
import { DateTime } from 'luxon';

import styles from './about.module.css';

import config from '@/config';

import {
	Dialog,
	DialogContent,
	dialogCardBackdropClassName,
	dialogCardClassName,
} from '@shared/ui/dialog';
import { cn } from '@/lib/utils';
import { useDialog, useNavbarIconUrl } from '@shared/lib/hooks';
import { sanitizeHtml } from '@/lib/article-renderer/core/sanitize-html';
import { T, useT } from '@shared/i18n';

export const About = ({ show, setShow }: AboutProps) => {
	const { title, version, about, buildNumber } = config.build;
	const t = useT();
	const navbarIconUrl = useNavbarIconUrl();

	// buildNumber is a Unix epoch (seconds) timestamp -- see
	// scripts/prepare.js's getBuildId().
	const buildDate = DateTime.fromSeconds(buildNumber).toLocaleString(
		DateTime.DATETIME_FULL,
	);
	const handleClick = (e: MouseEvent<HTMLSpanElement>) => {
		switch (e.detail) {
			case 2:
				window.location.replace('/drafts');
				break;
		}
	};

	// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
	const v = <span onClick={handleClick}> {version}</span>;

	return (
		<Dialog
			open={show}
			onOpenChange={(open) => {
				if (!open) {
					setShow(false);
				}
			}}
		>
			<DialogContent
				className={cn(dialogCardClassName, styles['container'])}
				backdropClassName={dialogCardBackdropClassName}
			>
				<div className={styles['about-dialog']}>
					<div className={styles['logo']}>
						<img src={navbarIconUrl} alt={t('common.logo')} />
					</div>
					<h3>{title}</h3>
					<h4>
						<T k="about.version" values={{ version: v }} />
					</h4>
					<h4 title={buildDate}>
						{t('about.build', { number: buildNumber })}
					</h4>
					{about ? (
						<div
							dangerouslySetInnerHTML={{
								__html: sanitizeHtml(about),
							}}
						/>
					) : null}
				</div>
			</DialogContent>
		</Dialog>
	);
};

interface AboutProps {
	show: boolean;
	setShow: (value: boolean) => void;
}

export function AboutButton() {
	const t = useT();
	const { setShowDialog } = useDialog();
	const className = [styles['about'], 'tooltip down cursor-pointer'].join(
		' ',
	);

	const onClick = () => {
		setShowDialog(true);
	};

	return (
		<button
			type="button"
			className={className}
			data-text={t('common.about')}
			aria-label={t('common.about')}
			onClick={onClick}
		>
			<Info color="var(--topbar-buttons-stroke)" />
		</button>
	);
}
